import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { useDebouncedCallback } from 'use-debounce';
import { activeViewportState, activeSeriesState } from 'config/recoilState';
import {
  VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
  useViewportsConfigurationsSelector,
  annotationsCacheState,
} from './state';
import { viewportIdToConfig } from '../Viewer/viewerUtils';
import type {
  ViewingContextPayload,
  ViewingViewportContext,
  ViewportsConfigurations,
} from './types';
import type { VtkAnnotation } from '../ViewportDre/Annotations/types';
import type { WithApolloContext } from 'modules/Apollo/types';
import { selectedToolState } from 'domains/viewer/Viewer/withSelectedTool';
import { buildActiveToolContext } from './viewerContextEnrichment';
import { buildAnnotationsContextFromRecoilState } from './annotationSummary';
import { broadcastViewerContext } from './viewerContextBroadcast';
import { logger } from 'modules/logger';

/**
 * Debounce time for broadcasting context changes (ms).
 * Prevents excessive broadcasts when multiple state changes happen rapidly.
 * Use 1-2 seconds to avoid impacting smooth scrolling performance.
 */
const BROADCAST_DEBOUNCE_MS = 1000;

/**
 * Hook that broadcasts viewer context to other windows whenever it changes.
 * Should be used in the viewer component to keep AI Sandbox updated with current state.
 *
 * This hook reads from various Recoil atoms and selectors to build the context payload,
 * then broadcasts it over a BroadcastChannel whenever changes are detected.
 *
 * @param context - Apollo context for queries
 * @param enabled - Whether broadcasting is enabled. Should only be true for Viewer 0
 *                  to prevent duplicate broadcasts from multiple viewer windows.
 */
export function useViewerContextBroadcaster({
  context,
  enabled = true,
}: WithApolloContext & { enabled?: boolean }): void {
  const viewportsConfigState = useViewportsConfigurationsSelector({
    defaults: VIEWPORT_CONFIGURATION_SELECTOR_DEFAULTS,
    context,
  });

  const viewportsConfig = useRecoilValue(viewportsConfigState);
  const activeViewportId = useRecoilValue(activeViewportState);
  const activeSeriesSmid = useRecoilValue(activeSeriesState);
  const selectedToolId = useRecoilValue(selectedToolState);
  const annotationsCache = useRecoilValue(annotationsCacheState);

  const lastBroadcastRef = useRef<string | null>(null);

  const debouncedBroadcast = useDebouncedCallback(
    (args: {
      viewportsConfig: Nullable<ViewportsConfigurations>;
      activeViewportId: string | null;
      activeSeriesSmid: string | null;
      selectedToolId: string | null;
      annotationsCache: ReadonlyArray<VtkAnnotation>;
    }) => {
      try {
        const payload = buildViewerContextPayload(args);

        // Only broadcast if payload has actually changed
        const payloadStr = payload != null ? JSON.stringify(payload) : null;
        if (payloadStr !== lastBroadcastRef.current) {
          lastBroadcastRef.current = payloadStr;
          broadcastViewerContext(payload);
          logger.debug('[ViewerContextBroadcaster] Broadcasted viewer context', {
            hasContext: payload != null,
            viewportCount: payload?.viewports?.length ?? 0,
          });
        }
      } catch (err) {
        logger.warn('[ViewerContextBroadcaster] Failed to build/broadcast context', err);
      }
    },
    BROADCAST_DEBOUNCE_MS,
    { leading: false, trailing: true }
  );

  useEffect(() => {
    if (!enabled) return;

    debouncedBroadcast({
      viewportsConfig,
      activeViewportId,
      activeSeriesSmid,
      selectedToolId,
      annotationsCache,
    });

    return () => {
      debouncedBroadcast.cancel();
    };
  }, [
    enabled,
    viewportsConfig,
    activeViewportId,
    activeSeriesSmid,
    selectedToolId,
    annotationsCache,
    debouncedBroadcast,
  ]);

  // Broadcast clear on unmount (only if enabled to avoid Viewer 1 clearing Viewer 0's context)
  useEffect(() => {
    if (!enabled) return;

    return () => {
      debouncedBroadcast.cancel();
      broadcastViewerContext(null);
    };
  }, [enabled, debouncedBroadcast]);
}

/**
 * Builds the viewer context payload from current state.
 */
function buildViewerContextPayload({
  viewportsConfig,
  activeViewportId,
  activeSeriesSmid,
  selectedToolId,
  annotationsCache,
}: {
  viewportsConfig: Nullable<ViewportsConfigurations>;
  activeViewportId: string | null;
  activeSeriesSmid: string | null;
  selectedToolId: string | null;
  annotationsCache: ReadonlyArray<VtkAnnotation>;
}): ViewingContextPayload | null {
  const configs = viewportsConfig ?? {};
  const entries = Object.entries(configs) as [string, ViewportsConfigurations[string]][];

  if (entries.length === 0) {
    return null;
  }

  // Process viewports synchronously (simplified from async snapshot version)
  // Note: Display configurations are not available synchronously in this version,
  // so we include what we can from the viewport config itself
  const viewports: ViewingViewportContext[] = entries
    .map(([viewportId, cfg]) => {
      if (!cfg || !cfg.series?.smid) return null;

      const { windowId, rowIndex, colIndex } = viewportIdToConfig(viewportId);

      const viewportBase: ViewingViewportContext = {
        viewport_id: viewportId,
        window_id: windowId,
        row_index: rowIndex,
        col_index: colIndex,
        study_id: cfg.study?.smid ?? null,
        series_id: cfg.series?.smid ?? null,
        stack_id: cfg.stack?.smid ?? null,
        view_type: cfg.viewType ?? null,
        comparison_index: cfg.comparisonIndex ?? null,
        // Display configs are not available synchronously - these will be null
        image_index: null,
        window: null,
        level: null,
        zoom: null,
        is_horizontally_flipped: undefined,
        is_vertically_flipped: undefined,
        is_inverted: undefined,
        position: undefined,
        focal_point: undefined,
        view_up: undefined,
        is_active: viewportId === activeViewportId,
      };

      const colorMap = cfg.colorMap;
      const colorAwareViewport: ViewingViewportContext = {
        ...viewportBase,
        color_map_preset: colorMap?.vtkColorMapPreset ?? null,
      };

      return colorAwareViewport;
    })
    .filter((v): v is ViewingViewportContext => v !== null);

  if (viewports.length === 0) {
    return null;
  }

  const activeSeriesId = activeSeriesSmid || viewports[0].series_id || null;
  const safeActiveViewportId =
    viewports.find((v) => v.viewport_id === activeViewportId)?.viewport_id ??
    viewports[0].viewport_id;

  const visibleSeriesIds = Array.from(
    new Set(
      viewports
        .flatMap((v) => [
          v.series_id ?? null,
          ...(v.fusion_layers?.map((l) => l.series_id ?? null) ?? []),
        ])
        .filter((x): x is string => !!x)
    )
  );

  const active_tool = selectedToolId != null ? buildActiveToolContext(selectedToolId) : null;

  const annotations = buildAnnotationsContextFromRecoilState({
    annotations: annotationsCache,
    visibleSeriesIds,
    activeSeriesId,
    measurementLimit: 50,
  });

  return {
    active_viewport_id: safeActiveViewportId,
    active_series_id: activeSeriesId,
    viewports,
    active_tool,
    annotations,
  };
}
